import { globalStores } from '@/editor/stores';
import { Group, Mesh, MeshBasicMaterial, Object3D, PlaneGeometry, Shape, ShapeGeometry } from 'three';
import BaseModel2d from '../BaseModel2d';
import { pixelRatio2d } from '@/editor/math/const';
import { Line2, LineGeometry, LineMaterial, LineSegmentsGeometry } from 'three/examples/jsm/Addons.js';
import { LineSegments2 } from 'three/examples/jsm/Addons.js';
import { EEntityType } from '@/editor/types';
import { EContainerType, EEntityStatus, ETransformType } from '@/editor/types/entity';
import { isNumber } from 'lodash-es';
import { setModelName, setModelVisible } from '../../utils';
import { ENTITY_LATER, ENTITY_PRIORITY, ENTITY_RENDER_ORDER } from '@/editor/constant/entity';
import { EEnableScaleMode, ITransformEntityParams } from '../../type';

const CIRCLE_RADIUS = 2;
const RECT_SIZE = 0.16;
const LINE_WIDTH = 1;

const DEFAULT_COLOR = 0x4c9dff;
const DEFAULT_COLOR_OPACITY = 0.6;
const ACTIVE_COLOR = 0x1a7af8;
const ACTIVE_COLOR_OPACITY = 1;

export default class TransformHelper extends BaseModel2d {
  private _bgMaterial: MeshBasicMaterial;
  private _lineMaterial: LineMaterial;

  private _arrow: Object3D | null = null;
  private _quarterCircle: Object3D | null = null;
  private _circle: Object3D | null = null;

  private _params: Omit<ITransformEntityParams, 'forceUpdate' | 'visible' | 'boxes'> = {
    center: { x: 0, y: 0 },
    width: 0,
    height: 0,
    rotation: 0,
    status: EEntityStatus.normal,
    transformType: ETransformType.Empty,
    enableRotation: true,
    enableScaleMode: EEnableScaleMode.Empty
  };

  constructor() {
    super();

    // this._modelGroup.userData.type = EEntityType.Transform;
    // this._modelGroup.userData.entityId = getUniqueId();
    // this._modelGroup.userData.containerType = EContainerType.entity;
    // this._modelGroup.userData.priority = ENTITY_PRIORITY.transformHelper;

    this.setDisplayOrder(ENTITY_RENDER_ORDER.transformHelper);

    globalStores.editorStateStore.sceneInfo.scene2d.intersectableGroup.add(this._modelGroup);

    this._bgMaterial = new MeshBasicMaterial({
      color: DEFAULT_COLOR,
      opacity: DEFAULT_COLOR_OPACITY,
      transparent: true
    });
    this._lineMaterial = new LineMaterial({
      color: ACTIVE_COLOR,
      transparent: true,
      linewidth: LINE_WIDTH // 宽度（需启用渲染器支持）
    });
  }

  update(params: Partial<ITransformEntityParams>) {
    this._params = { ...this._params, ...params };
    this.clear();
    this.createModel();
    this.updateWithoutDestroy(params);
  }

  updateWithoutDestroy(params: Partial<ITransformEntityParams>) {
    this._params = { ...this._params, ...params };
    const { center, rotation, status, transformType } = params;

    if (!this._modelGroup.children.length) this.createModel();

    if (center) {
      this._modelGroup.position.setX(center.x / pixelRatio2d);
      this._modelGroup.position.setY(center.y / pixelRatio2d);
    }
    if (isNumber(rotation)) {
      this._modelGroup.rotation.z = rotation;
      if (this._circle) this._circle.rotation.z = -rotation;
    }

    let arrowVisible = false;
    let circleVisible = false;
    let quarterCircleVisible = false;
    let bgColor = DEFAULT_COLOR;
    let bgColorOpacity = DEFAULT_COLOR_OPACITY;

    if (status === EEntityStatus.hover && transformType === ETransformType.Rotation) {
      arrowVisible = true;
      circleVisible = false;
      quarterCircleVisible = false;
      bgColor = ACTIVE_COLOR;
      bgColorOpacity = ACTIVE_COLOR_OPACITY;
    } else if (status === EEntityStatus.selected && transformType === ETransformType.Rotation) {
      arrowVisible = false;
      circleVisible = true;
      quarterCircleVisible = true;
      bgColor = ACTIVE_COLOR;
      bgColorOpacity = ACTIVE_COLOR_OPACITY;
    } else {
      arrowVisible = true;
      circleVisible = false;
      quarterCircleVisible = false;
      bgColor = DEFAULT_COLOR;
      bgColorOpacity = DEFAULT_COLOR_OPACITY;
    }

    if (this._arrow) setModelVisible(this._arrow, arrowVisible);
    if (this._circle) setModelVisible(this._circle, circleVisible);
    if (this._quarterCircle) setModelVisible(this._quarterCircle, quarterCircleVisible);
    this._bgMaterial.color.set(bgColor);
    this._bgMaterial.opacity = bgColorOpacity;
  }

  createModel() {
    const { center, rotation, enableScaleMode, enableRotation } = this._params;

    this._modelGroup.add(this.createBoundingOutline());

    if (enableRotation) {
      const rotationModel = this.createRotationModel();
      setTransformPartEntityData(rotationModel, ETransformType.Rotation);
      this._modelGroup.add(rotationModel);
    }

    if (enableScaleMode !== EEnableScaleMode.Empty) {
      const scaleRect = this.createScaleRect();
      Object.entries(scaleRect).forEach(([transformType, object]) => {
        setTransformPartEntityData(object, transformType as ETransformType);
        this._modelGroup.add(object);
      });
    }

    this._modelGroup.position.setX(center.x / pixelRatio2d);
    this._modelGroup.position.setY(center.y / pixelRatio2d);
    this._modelGroup.rotation.z = rotation;
  }

  private createRotationModel() {
    const group = new Group();

    const zoom = globalStores.scene2dZoom / 100;
    const ringDeltaRadius = 0.12 / zoom;
    const centerRadius = (CIRCLE_RADIUS - ringDeltaRadius) / zoom;
    const startAngle = (Math.PI * 5) / 4;
    const endAngle = (Math.PI * 7) / 4;
    const arrowDeltaAngle = Math.PI / 14;
    const arrowDeltaRadius = 0.24 / zoom;

    this._arrow = this.createRotationArrow({
      centerRadius,
      ringDeltaRadius,
      startAngle,
      endAngle,
      arrowDeltaAngle,
      arrowDeltaRadius
    });
    this._quarterCircle = this.createRotationQuarterCircle({
      centerRadius,
      ringDeltaRadius,
      startAngle,
      endAngle
    });

    this._circle = this.createRotationCircle({ centerRadius, ringDeltaRadius });

    setModelName(this._arrow, ETransformType.Rotation);
    setModelName(this._quarterCircle, ETransformType.Rotation);

    group.add(this._circle, this._quarterCircle, this._arrow);

    return group;
  }

  private createRotationArrow(params: {
    centerRadius: number;
    ringDeltaRadius: number;
    startAngle: number;
    endAngle: number;
    arrowDeltaAngle: number;
    arrowDeltaRadius: number;
  }) {
    const { centerRadius, startAngle, endAngle, arrowDeltaAngle, arrowDeltaRadius, ringDeltaRadius } = params;
    const group = new Group();

    const shape = new Shape();
    shape.moveTo(centerRadius * Math.cos(startAngle), centerRadius * Math.sin(startAngle));
    shape.lineTo(
      (centerRadius - arrowDeltaRadius) * Math.cos(startAngle + arrowDeltaAngle),
      (centerRadius - arrowDeltaRadius) * Math.sin(startAngle + arrowDeltaAngle)
    );
    shape.lineTo(
      (centerRadius - ringDeltaRadius) * Math.cos(startAngle + arrowDeltaAngle),
      (centerRadius - ringDeltaRadius) * Math.sin(startAngle + arrowDeltaAngle)
    );
    shape.absarc(0, 0, centerRadius - ringDeltaRadius, startAngle + arrowDeltaAngle, endAngle - arrowDeltaAngle, false);
    shape.lineTo(
      (centerRadius - arrowDeltaRadius) * Math.cos(endAngle - arrowDeltaAngle),
      (centerRadius - arrowDeltaRadius) * Math.sin(endAngle - arrowDeltaAngle)
    );
    shape.lineTo(centerRadius * Math.cos(endAngle), centerRadius * Math.sin(endAngle));
    shape.lineTo(
      (centerRadius + arrowDeltaRadius) * Math.cos(endAngle - arrowDeltaAngle),
      (centerRadius + arrowDeltaRadius) * Math.sin(endAngle - arrowDeltaAngle)
    );
    shape.lineTo(
      (centerRadius + ringDeltaRadius) * Math.cos(endAngle - arrowDeltaAngle),
      (centerRadius + ringDeltaRadius) * Math.sin(endAngle - arrowDeltaAngle)
    );
    shape.absarc(0, 0, centerRadius + ringDeltaRadius, endAngle - arrowDeltaAngle, startAngle + arrowDeltaAngle, true);
    shape.lineTo(
      (centerRadius + arrowDeltaRadius) * Math.cos(startAngle + arrowDeltaAngle),
      (centerRadius + arrowDeltaRadius) * Math.sin(startAngle + arrowDeltaAngle)
    );
    shape.lineTo(centerRadius * Math.cos(startAngle), centerRadius * Math.sin(startAngle));

    const shapeMesh = new Mesh(new ShapeGeometry(shape), this._bgMaterial);

    const outlineGeometry = new LineGeometry();
    outlineGeometry.setFromPoints(shape.getPoints());
    const outLineMesh = new Line2(
      outlineGeometry,
      new LineMaterial({
        color: ACTIVE_COLOR,
        transparent: true,
        linewidth: 0.5
      })
    );
    group.add(shapeMesh, outLineMesh);

    return group;
  }
  private createRotationQuarterCircle(params: {
    centerRadius: number;
    ringDeltaRadius: number;
    startAngle: number;
    endAngle: number;
  }) {
    const { centerRadius, startAngle, endAngle, ringDeltaRadius } = params;

    const shape = new Shape();
    shape.moveTo(
      (centerRadius + ringDeltaRadius) * Math.cos(startAngle),
      (centerRadius + ringDeltaRadius) * Math.sin(startAngle)
    );
    shape.absarc(0, 0, centerRadius + ringDeltaRadius, startAngle, endAngle, false);
    shape.lineTo(
      (centerRadius - ringDeltaRadius) * Math.cos(endAngle),
      (centerRadius - ringDeltaRadius) * Math.sin(endAngle)
    );
    shape.absarc(0, 0, centerRadius - ringDeltaRadius, endAngle, startAngle, true);
    shape.lineTo(
      (centerRadius + ringDeltaRadius) * Math.cos(startAngle),
      (centerRadius + ringDeltaRadius) * Math.sin(startAngle)
    );

    return new Mesh(new ShapeGeometry(shape), this._bgMaterial);
  }
  private createRotationCircle(params: { centerRadius: number; ringDeltaRadius: number }) {
    const { centerRadius, ringDeltaRadius } = params;
    const shape = new Shape();

    shape.moveTo(centerRadius + ringDeltaRadius, 0);
    shape.absarc(0, 0, centerRadius + ringDeltaRadius, 0, Math.PI * 2, false);
    shape.moveTo(centerRadius - ringDeltaRadius, 0);
    shape.absarc(0, 0, centerRadius - ringDeltaRadius, 0, Math.PI * 2, true);

    const circle = new Mesh(
      new ShapeGeometry(shape, 64),
      new MeshBasicMaterial({
        color: DEFAULT_COLOR,
        opacity: DEFAULT_COLOR_OPACITY,
        transparent: true
      })
    );

    // 添加8条射线
    const radiation = this.createRotationCircleRadiation({ centerRadius, ringDeltaRadius });
    radiation.position.setZ(1); // 提升到circle的上方
    circle.add(radiation);

    return circle;
  }
  private createRotationCircleRadiation(params: { centerRadius: number; ringDeltaRadius: number }) {
    const { centerRadius, ringDeltaRadius } = params;
    const group = new Group();
    const line = new LineGeometry();
    line.setPositions([centerRadius - ringDeltaRadius, 0, 0, centerRadius + ringDeltaRadius, 0, 0]);
    const line0 = new Line2(line, new LineMaterial({ color: 0xffffff, linewidth: 1 }));

    const line45 = line0.clone();
    line45.rotation.z = Math.PI / 4;
    const line90 = line0.clone();
    line90.rotation.z = Math.PI / 2;
    const line135 = line0.clone();
    line135.rotation.z = (3 * Math.PI) / 4;
    const line180 = line0.clone();
    line180.rotation.z = Math.PI;
    const line225 = line0.clone();
    line225.rotation.z = (5 * Math.PI) / 4;
    const line270 = line0.clone();
    line270.rotation.z = (3 * Math.PI) / 2;
    const line315 = line0.clone();
    line315.rotation.z = (7 * Math.PI) / 4;
    group.add(line0, line45, line90, line135, line180, line225, line270, line315);

    return group;
  }

  private createScaleRect() {
    const { enableScaleMode, width, height } = this._params;
    const scaleObjects: Partial<Record<ETransformType, Object3D>> = {};

    const addCorner = enableScaleMode === EEnableScaleMode.Corner || enableScaleMode === EEnableScaleMode.All;
    const addLeftRight =
      enableScaleMode === EEnableScaleMode.LeftRight ||
      enableScaleMode === EEnableScaleMode.Edge ||
      enableScaleMode === EEnableScaleMode.All;
    const addTopBottom = enableScaleMode === EEnableScaleMode.Edge || enableScaleMode === EEnableScaleMode.All;

    if (addCorner) {
      const leftTopRect = this.createRect();
      leftTopRect.position.set(-width / 2 / pixelRatio2d, height / 2 / pixelRatio2d, 0);
      scaleObjects[ETransformType.ScaleLeftTop] = leftTopRect;

      const leftBottomRect = this.createRect();
      leftBottomRect.position.set(-width / 2 / pixelRatio2d, -height / 2 / pixelRatio2d, 0);
      scaleObjects[ETransformType.ScaleLeftBottom] = leftBottomRect;

      const rightTopRect = this.createRect();
      rightTopRect.position.set(width / 2 / pixelRatio2d, height / 2 / pixelRatio2d, 0);
      scaleObjects[ETransformType.ScaleRightTop] = rightTopRect;

      const rightBottomRect = this.createRect();
      rightBottomRect.position.set(width / 2 / pixelRatio2d, -height / 2 / pixelRatio2d, 0);
      scaleObjects[ETransformType.ScaleRightBottom] = rightBottomRect;
    }

    if (addLeftRight) {
      const leftRect = this.createRect();
      leftRect.position.set(-width / 2 / pixelRatio2d, 0, 0);
      scaleObjects[ETransformType.ScaleLeft] = leftRect;

      const rightRect = this.createRect();
      rightRect.position.set(width / 2 / pixelRatio2d, 0, 0);
      scaleObjects[ETransformType.ScaleRight] = rightRect;
    }

    if (addTopBottom) {
      const topRect = this.createRect();
      topRect.position.set(0, height / 2 / pixelRatio2d, 0);
      scaleObjects[ETransformType.ScaleTop] = topRect;

      const bottomRect = this.createRect();
      bottomRect.position.set(0, -height / 2 / pixelRatio2d, 0);
      scaleObjects[ETransformType.ScaleBottom] = bottomRect;
    }

    return scaleObjects;
  }
  private createRect() {
    const zoom = globalStores.scene2dZoom / 100;
    const width = RECT_SIZE / zoom;
    const height = RECT_SIZE / zoom;
    const group = new Group();

    const bgPlane = new PlaneGeometry(width, height);
    const bgMesh = new Mesh(
      bgPlane,
      new MeshBasicMaterial({
        color: 0x000000,
        opacity: 0,
        transparent: true
      })
    );
    group.add(bgMesh);

    const lineGeometry = new LineGeometry();
    lineGeometry.setPositions([
      -width / 2,
      -height / 2,
      0,
      width / 2,
      -height / 2,
      0,
      width / 2,
      height / 2,
      0,
      -width / 2,
      height / 2,
      0,
      -width / 2,
      -height / 2,
      0
    ]);
    const rectMesh = new Line2(lineGeometry, this._lineMaterial);
    rectMesh.layers.set(ENTITY_LATER.TRANSFORM_LAYER);
    group.add(rectMesh);

    return group;
  }

  private createBoundingOutline() {
    const { width, height, enableScaleMode } = this._params;
    const lineGmt = new LineSegmentsGeometry();
    const lineArray = [];
    const zPosition = 0;

    const lineMaterial = this._lineMaterial;
    const zoom = globalStores.scene2dZoom / 100;
    const corderOffset =
      enableScaleMode === EEnableScaleMode.Corner || enableScaleMode === EEnableScaleMode.All
        ? RECT_SIZE / zoom / 2
        : 0;
    const leftRightOffset =
      enableScaleMode === EEnableScaleMode.LeftRight ||
      enableScaleMode === EEnableScaleMode.Edge ||
      enableScaleMode === EEnableScaleMode.All
        ? RECT_SIZE / zoom / 2
        : 0;
    const topBottomOffset =
      enableScaleMode === EEnableScaleMode.Edge || enableScaleMode === EEnableScaleMode.All ? RECT_SIZE / zoom / 2 : 0;

    // top line
    lineArray.push(
      -(width / pixelRatio2d) / 2 + corderOffset,
      height / pixelRatio2d / 2,
      zPosition,

      -topBottomOffset,
      height / pixelRatio2d / 2,
      zPosition,

      topBottomOffset,
      height / pixelRatio2d / 2,
      zPosition,

      width / pixelRatio2d / 2 - corderOffset,
      height / pixelRatio2d / 2,
      zPosition
    );

    // bottom line
    lineArray.push(
      -(width / pixelRatio2d) / 2 + corderOffset,
      -height / pixelRatio2d / 2,
      zPosition,

      -topBottomOffset,
      -height / pixelRatio2d / 2,
      zPosition,

      topBottomOffset,
      -height / pixelRatio2d / 2,
      zPosition,

      width / pixelRatio2d / 2 - corderOffset,
      -height / pixelRatio2d / 2,
      zPosition
    );

    // left line
    lineArray.push(
      -width / pixelRatio2d / 2,
      -(height / pixelRatio2d) / 2 + corderOffset,
      zPosition,

      -width / pixelRatio2d / 2,
      -leftRightOffset,
      zPosition,

      -width / pixelRatio2d / 2,
      leftRightOffset,
      zPosition,

      -width / pixelRatio2d / 2,
      height / pixelRatio2d / 2 - corderOffset,
      zPosition
    );

    // right line
    lineArray.push(
      width / pixelRatio2d / 2,
      -(height / pixelRatio2d) / 2 + corderOffset,
      zPosition,

      width / pixelRatio2d / 2,
      -leftRightOffset,
      zPosition,

      width / pixelRatio2d / 2,
      leftRightOffset,
      zPosition,

      width / pixelRatio2d / 2,
      height / pixelRatio2d / 2 - corderOffset,
      zPosition
    );

    lineGmt.setPositions(lineArray);
    return new LineSegments2(lineGmt, lineMaterial);
  }

  override clear() {
    this._arrow = null;
    this._quarterCircle = null;
    this._circle = null;

    super.clear();
  }
}

function setTransformPartEntityData(mesh: Object3D, transformType: ETransformType) {
  mesh.userData.entityId = transformType;
  mesh.userData.type = EEntityType.Transform;
  mesh.userData.containerType = EContainerType.entity;
  mesh.userData.priority = ENTITY_PRIORITY.transformHelper;
  mesh.userData.transformType = transformType;
}
